import { ChangeEvent, FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSaveInquiry } from '@/api/contact';
import mainBanner from '@/assets/img/banner/contact.jpg';
import SubBanner from '@/components/common/Banner/SubBanner/SubBanner';
import InputFiles from '@/components/common/Input/InputFiles/InputFiles';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface FormData {
	name: string;
	emailId: string;
	emailDomain: string;
	emailDomainSelect: string;
	phone: string;
	title: string;
	content: string;
}
interface FormErrors {
	name?: boolean;
	email?: boolean;
	phone?: boolean;
	title?: boolean;
	content?: boolean;
	[key: string]: boolean | undefined;
}

const Contact = () => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		emailId: '',
		emailDomain: '',
		emailDomainSelect: '',
		phone: '',
		title: '',
		content: '',
	});

	const [errors, setErrors] = useState<FormErrors>({});
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

	const { openToast } = useToastStore();
	const navigate = useNavigate();

	const { mutateAsync: saveInquiry } = useSaveInquiry();

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'phone') {
			const numericValue = value.replace(/\D/g, '');
			setFormData({ ...formData, [name]: numericValue });
		} else {
			setFormData({ ...formData, [name]: value });
		}

		if (submitted) {
			const newErrors = { ...errors };
			if (name === 'name' && value) delete newErrors.name;
			if ((name === 'emailId' || name === 'emailDomain') && formData.emailId && formData.emailDomain) delete newErrors.email;
			if (name === 'phone' && value) delete newErrors.phone;
			if (name === 'title' && value) delete newErrors.title;
			if (name === 'content' && value) delete newErrors.content;
			setErrors(newErrors);
		}
	};

	const validateForm = (): FormErrors => {
		const newErrors: FormErrors = {};

		if (!formData.name) newErrors.name = true;
		if (!formData.emailId || !formData.emailDomain) newErrors.email = true;
		if (!formData.phone) newErrors.phone = true;
		if (!formData.title) newErrors.title = true;
		if (!formData.content) newErrors.content = true;
		return newErrors;
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const formErrors = validateForm();

		setErrors(formErrors);
		setSubmitted(true);

		const isValid = Object.keys(formErrors).length === 0;

		if (isValid) {
			const form = new FormData();
			form.append('inquiry_name', formData.name);
			form.append('inquiry_email', `${formData.emailId}@${formData.emailDomain}`);
			form.append('inquiry_cell', formData.phone);
			form.append('inquiry_title', formData.title);
			form.append('inquiry_content', formData.content);
			uploadedFiles.forEach((file, index) => {
				form.append(`file[${index}]`, file);
			});

			try {
				const res = await saveInquiry(form);
				if (res.data.success) {
					openToast({
						icon: 'alert',
						content: '제출이 완료되었습니다.',
						showTime: 3000,
					});

					setTimeout(() => {
						navigate('/');
					}, 3000);
				} else {
					openToast({
						icon: 'alert',
						content: '제출에 실패하였습니다.',
						showTime: 3000,
					});
				}
			} catch (error) {
				console.error('Error:', error);
			}

			setFormData({
				name: '',
				emailId: '',
				emailDomain: '',
				emailDomainSelect: '',
				phone: '',
				title: '',
				content: '',
			});
			setSubmitted(false);
		} else {
			console.log('폼 검증 실패');
		}
	};

	return (
		<main>
			<SubBanner subTitle="Contact" title="문의하기" img={mainBanner} />
			<S.Container>
				<S.FormContainer>
					<S.Form onSubmit={handleSubmit} noValidate>
						<S.FormGroup>
							<S.Label htmlFor="name">
								이름<S.Required>*</S.Required>
							</S.Label>
							<S.Input
								type="text"
								id="name"
								name="name"
								placeholder="이름 입력"
								value={formData.name}
								onChange={handleChange}
								error={submitted && errors.name}
								maxLength={20}
							/>
							{submitted && errors.name && <S.ErrorText>이름을 입력해 주세요.</S.ErrorText>}
						</S.FormGroup>

						<S.FormGroup>
							<S.Label htmlFor="email">
								이메일<S.Required>*</S.Required>
							</S.Label>
							<S.EmailGroup>
								<S.EmailInput
									type="text"
									id="emailId"
									name="emailId"
									placeholder="이메일 주소 입력"
									value={formData.emailId}
									onChange={handleChange}
									error={submitted && errors.email}
									fullWidth
								/>
								<S.EmailAt>@</S.EmailAt>
								<S.EmailInput
									type="text"
									id="emailDomain"
									name="emailDomain"
									placeholder="도메인 입력"
									value={formData.emailDomain}
									onChange={handleChange}
									error={submitted && errors.email}
									fullWidth
									disabled={!!formData.emailDomainSelect}
								/>
								<InputSelect
									options={[
										{
											label: '직접입력',
											value: '',
										},
										{
											label: 'gmail.com',
											value: 'gmail.com',
										},
										{
											label: 'naver.com',
											value: 'naver.com',
										},
										{
											label: 'kakao.com',
											value: 'kakao.com',
										},
									]}
									selectedOption={formData.emailDomainSelect}
									setSelectedOption={(label: string) => {
										setFormData({ ...formData, emailDomain: label, emailDomainSelect: label });
									}}
									defaultValue="직접입력"
								/>
							</S.EmailGroup>
							{submitted && errors.email && <S.ErrorText>이메일을 입력해 주세요.</S.ErrorText>}
						</S.FormGroup>

						<S.FormGroup>
							<S.Label htmlFor="phone">
								전화번호<S.Required>*</S.Required>
							</S.Label>
							<S.Input
								type="tel"
								id="phone"
								name="phone"
								placeholder="'-'없이 숫자만 입력"
								value={formData.phone}
								onChange={handleChange}
								error={submitted && errors.phone}
								maxLength={11}
							/>
							{submitted && errors.phone && <S.ErrorText>전화번호를 입력해 주세요.</S.ErrorText>}
						</S.FormGroup>

						<S.FormGroup>
							<S.Label htmlFor="title">
								제목<S.Required>*</S.Required>
							</S.Label>
							<S.Input
								type="text"
								id="title"
								name="title"
								placeholder="제목 입력"
								value={formData.title}
								onChange={handleChange}
								error={submitted && errors.title}
								maxLength={50}
							/>
							{submitted && errors.title && <S.ErrorText>제목을 입력해 주세요.</S.ErrorText>}
						</S.FormGroup>

						<S.FormGroup>
							<S.Label htmlFor="content">
								내용<S.Required>*</S.Required>
							</S.Label>
							<S.Textarea id="content" name="content" placeholder="내용 입력" value={formData.content} onChange={handleChange} maxLength={2000} />
							{submitted && errors.content && <S.ErrorText>내용을 입력해 주세요.</S.ErrorText>}
						</S.FormGroup>

						<S.FormGroup>
							<S.Label htmlFor="files">첨부파일</S.Label>
							<InputFiles files={uploadedFiles} setFiles={setUploadedFiles} />
						</S.FormGroup>

						<S.ButtonArea>
							<S.Button
								className="primary"
								type="submit"
								disabled={!formData.name || !formData.emailId || !formData.emailDomain || !formData.title || !formData.phone || !formData.content}
							>
								등록
							</S.Button>
						</S.ButtonArea>
					</S.Form>
				</S.FormContainer>
			</S.Container>
		</main>
	);
};

export default Contact;
